// @ts-check

/**
 * @file Renders the users index route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import Link from "../../src/components/link"
import TextInput from "../../src/components/text-input"
import Button from "../../src/components/button"
import buildRoutePath from "../../src/shared/build-route-path"
import Card from "../../src/components/card"
import EmptyState from "../../src/components/empty-state"
import errorMessage from "../../src/shared/error-message"
import LoadingState from "../../src/components/loading-state"
import Modal from "../../src/components/modal"
import Pagination from "../../src/components/pagination"
import {User} from "../../src/frontend-models/user"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30

/** @typedef {{[key: string]: unknown}} UsersIndexScreenProps */

/**
 * @typedef {object} UsersIndexScreenState
 * @property {string | null} confirmingDeleteUserId User selected for delete confirmation.
 * @property {boolean} deletingUser Whether a delete request is running.
 * @property {boolean} loading Whether users are loading.
 * @property {number} page Current result page.
 * @property {string} searchQuery Current search filter text.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown above the list.
 * @property {number} totalCount Number of matching users.
 * @property {User[]} users Loaded users for the active page.
 */

/**
 * Renders user search, result cards, delete confirmation, and pagination.
 * @augments {ShapeComponent<UsersIndexScreenProps, UsersIndexScreenState>}
 */
class UsersIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  handlers = {}
  loadRequestId = 0
  searchDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ null
  /** @type {UsersIndexScreenState} */
  state = {
    confirmingDeleteUserId: /** @type {string | null} */ (null),
    deletingUser: false,
    loading: true,
    page: 1,
    searchQuery: "",
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    totalCount: 0,
    users: /** @type {User[]} */ []
  }

  /**
   * Initializes translations, route params, and loading effects.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const params = useLocalSearchParams()

    this.params = params
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const page = this.s.page
    const searchQuery = this.s.searchQuery
    useEffect(() => {
      void this.loadUsers()
    }, [page, searchQuery])
    useEffect(() => {
      return () => {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the users index route.
   * @returns {ReactNode} Users index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="usersIndexScreen"
      >
        <ScreenTitle>{_("Users")}</ScreenTitle>
        <View
          testID="usersIndexScreen/content"
        >
          <View
            style={styles.titleRow ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 16
            }}
            testID="usersIndexScreen/titleRow"
          >
            <Text
              style={styles[`screenTitle-${this.tt.themeName}`] ||= {
                fontSize: 24,
                fontWeight: 700
              }}
              testID="usersIndexScreen/title"
            >
              {_("Users")}
            </Text>
            <Button
              href={buildRoutePath(this.tt.params, "/users/new")}
              iconName="plus"
              label={_("New user")}
              testID="usersIndexScreen/newButton"
            />
          </View>
          <TextInput
            onChangeText={this.tt.onSearchChangeText}
            placeholder={_("Search users...")}
            style={styles.searchInput ||= {borderRadius: 8, marginBottom: 16}}
            testID="usersIndexScreen/searchInput"
          />
          {status &&
            <Text
              style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="usersIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading && <LoadingState testID="usersIndexScreen/loadingText" />}
          {!loading && this.renderUsersList()}
          {this.renderPagination()}
        </View>
        {this.renderDeleteConfirmModal()}
      </View>
    )
  }

  /**
   * Renders pagination controls.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, totalCount} = this.s
    if (loading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="usersIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Renders user result cards.
   * @returns {ReactNode} User cards or empty state.
   */
  renderUsersList() {
    const {users} = this.s
    if (users.length === 0) {
      return (
        <EmptyState
          message={_("No users found")}
          testID="usersIndexScreen/emptyCard"
        />
      )
    }
    return (
      <View
        style={styles.list ||= {
          gap: 10
        }}
        testID="usersIndexScreen/list"
      >
        {users.map((user) => {
          const userId = user.id()
          const statusKey = statusKeyForUser(user)
          const themeName = this.tt.themeName
          const colors = this.tt.colors
          const statusBadgeBackground =
            statusKey === "active"
              ? colors.successBackground
              : statusKey === "invited"
                ? colors.infoBackground
                : colors.surfaceMuted
          const statusBadgeText =
            statusKey === "active"
              ? colors.successText
              : statusKey === "invited"
                ? colors.infoText
                : colors.textMuted
          return (
            <Card
              key={userId}
              style={styles.userCardSpacing ||= {marginBottom: 8}}
              testID={`usersIndexScreen/user-${userId}`}
            >
              <View
                style={styles.userHeader ||= {
                  alignItems: "flex-start",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 12
                }}
                testID={`usersIndexScreen/user-${userId}/header`}
              >
                <View
                  style={styles.userIdentity ||= {
                    flex: 1,
                    paddingRight: 12
                  }}
                  testID={`usersIndexScreen/user-${userId}/identity`}
                >
                  <Link
                    href={buildRoutePath(this.tt.params, `/users/${userId}`)}
                    testID={`usersIndexScreen/user-${userId}/showLink`}
                  >
                    <Text
                      style={styles[`userName-${themeName}`] ||= {
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      testID={`usersIndexScreen/user-${userId}/name`}
                    >
                      {displayNameForUser(_, user)}
                    </Text>
                  </Link>
                  <Text
                    style={styles[`userEmail-${themeName}`] ||= {
                      color: colors.textMuted,
                      marginTop: 2
                    }}
                    testID={`usersIndexScreen/user-${userId}/email`}
                  >
                    {user.email() || "-"}
                  </Text>
                </View>
                <View
                  style={styles[`statusBadge-${themeName}-${statusKey}`] ||= {
                    alignItems: "center",
                    alignSelf: "flex-start",
                    backgroundColor: statusBadgeBackground,
                    borderRadius: 999,
                    paddingHorizontal: 10,
                    paddingVertical: 4
                  }}
                  testID={`usersIndexScreen/user-${userId}/statusBadge`}
                >
                  <Text
                    style={styles[`statusBadgeText-${themeName}-${statusKey}`] ||= {
                      color: statusBadgeText,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase"
                    }}
                    testID={`usersIndexScreen/user-${userId}/statusText`}
                  >
                    {statusTextForUser(_, user)}
                  </Text>
                </View>
              </View>
              <View
                style={styles.userMeta ||= {
                  gap: 4,
                  marginBottom: 12
                }}
                testID={`usersIndexScreen/user-${userId}/meta`}
              >
                <Text
                  style={styles[`userMetaText-${themeName}`] ||= {
                    color: colors.textMuted,
                    fontSize: 13
                  }}
                  testID={`usersIndexScreen/user-${userId}/role`}
                >
                  {_("Member")}
                </Text>
                <Text
                  style={styles[`userMetaText-${themeName}`]}
                  testID={`usersIndexScreen/user-${userId}/lastLogin`}
                >
                  {lastLoginText(_, user)}
                </Text>
              </View>
              <View
                style={styles.userActions ||= {
                  flexDirection: "row",
                  gap: 10
                }}
                testID={`usersIndexScreen/user-${userId}/actions`}
              >
                <Button
                  href={buildRoutePath(this.tt.params, `/users/${userId}`)}
                  iconName="arrow-up-right-from-square"
                  label={_("Show")}
                  testID={`usersIndexScreen/user-${userId}/openButton`}
                  variant="outline"
                />
                <Button
                  href={buildRoutePath(this.tt.params, `/users/${userId}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID={`usersIndexScreen/user-${userId}/editButton`}
                  variant="outline"
                />
                <Button
                  iconName="trash"
                  label={_("Delete")}
                  onPress={
                    this.handlers[`confirmDelete-${userId}`] ||= () => {
                      this.s.confirmingDeleteUserId = userId
                      this.s.status = null
                    }
                  }
                  testID={`usersIndexScreen/user-${userId}/deleteButton`}
                  variant="destructive-outline"
                />
              </View>
            </Card>
          )
        })}
      </View>
    )
  }

  /**
   * Renders the delete confirmation modal.
   * @returns {ReactNode} Delete confirmation modal or null.
   */
  renderDeleteConfirmModal() {
    const {confirmingDeleteUserId, deletingUser} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Modal
        cardStyle={styles.deleteModalCardSpacing ||= {
          borderRadius: 12,
          maxWidth: 360,
          padding: 16
        }}
        isVisible={Boolean(confirmingDeleteUserId)}
        onRequestClose={this.tt.onDeleteCancelPress}
        testID="usersIndexScreen/deleteConfirmModal"
      >
          <Text
            style={styles[`deleteModalTitle-${themeName}`] ||= {
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 8
            }}
            testID="usersIndexScreen/deleteConfirmModal/title"
          >
            {_("Delete user")}
          </Text>
          <Text
            style={styles[`deleteModalBody-${themeName}`] ||= {
              color: colors.textMuted,
              marginBottom: 16
            }}
            testID="usersIndexScreen/deleteConfirmModal/body"
          >
            {_("Delete this user?")}
          </Text>
          <View
            style={styles.deleteModalActions ||= {
              flexDirection: "row",
              gap: 10,
              justifyContent: "flex-end"
            }}
            testID="usersIndexScreen/deleteConfirmModal/actions"
          >
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onDeleteCancelPress}
              testID="usersIndexScreen/deleteConfirmModal/cancelButton"
              variant="outline"
            />
            <Button
              disabled={deletingUser}
              iconName="trash"
              label={deletingUser ? _("Deleting...") : _("Delete")}
              onPress={this.tt.onDeleteConfirmPress}
              testID="usersIndexScreen/deleteConfirmModal/confirmButton"
              variant="destructive"
            />
          </View>
      </Modal>
    )
  }

  /**
   * Debounces search text updates.
   * @param {string} value Search input value.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      if (value !== this.s.searchQuery) {
        this.s.page = 1
        this.s.searchQuery = value
      }
    }, 300)
  }

  /**
   * Applies the selected result page.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Closes the delete confirmation modal.
   * @returns {void} Nothing.
   */
  onDeleteCancelPress = () => {
    this.s.confirmingDeleteUserId = null
    this.s.deletingUser = false
  }

  /**
   * Deletes the confirmed user.
   * @returns {Promise<void>} Resolves after delete handling finishes.
   */
  onDeleteConfirmPress = async () => {
    const {confirmingDeleteUserId, users} = this.s
    if (confirmingDeleteUserId === null) return
    this.s.deletingUser = true
    try {
      const user = users.find((candidate) => candidate.id() === confirmingDeleteUserId)
      if (!user) {
        this.s.confirmingDeleteUserId = null
        this.s.deletingUser = false
        return
      }
      await user.destroy()
      this.s.confirmingDeleteUserId = null
      this.s.deletingUser = false
      this.s.status = {
        kind: "success",
        text: _("The user was deleted")
      }
      const remaining = users.filter(
        (candidate) => candidate.id() !== confirmingDeleteUserId
      )
      this.s.users = remaining
      this.s.totalCount = Math.max(0, this.s.totalCount - 1)
      if (remaining.length === 0 && this.s.page > 1) {
        this.s.page -= 1
      }
    } catch (error) {
      this.s.confirmingDeleteUserId = null
      this.s.deletingUser = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Loads users for the active search and page.
   * @returns {Promise<void>} Resolves after users are loaded.
   */
  loadUsers = async () => {
    const {page, searchQuery} = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      /** @type {Record<string, unknown>} */
      const ransackParams = {
        s: "first_name asc, last_name asc, id asc"
      }
      if (searchQuery) ransackParams.emailCont = searchQuery
      const baseQuery = User.ransack(ransackParams)
      const [users, totalCount] = await Promise.all([
        baseQuery.clone().page(page).perPage(PAGE_SIZE).toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.totalCount = totalCount
      this.s.users = users
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<UsersIndexScreenProps>} */
const UsersIndexScreenComponent = memo(shapeComponent(UsersIndexScreen))

export default UsersIndexScreenComponent

/**
 * Builds the best display name for a user.
 * @param {(text: string) => string} _ Translation function.
 * @param {User} user User to label.
 * @returns {string} User display label.
 */
function displayNameForUser(_, user) {
  const combinedName = `${user.firstName() || ""} ${user.lastName() || ""}`.trim()
  if (combinedName) return combinedName
  const name = user.name()
  if (name && name.trim()) return name.trim()
  const email = user.email()
  if (email && email.trim()) return email.trim()
  return _("Unknown user")
}

/**
 * Resolves a user status key.
 * @param {User} user User to inspect.
 * @returns {"active" | "inactive" | "invited"} Status key for display.
 */
function statusKeyForUser(user) {
  const active = user.active()
  if (active === true) return "active"
  if (active === false) return "inactive"
  return "invited"
}

/**
 * Builds the translated user status label.
 * @param {(text: string) => string} _ Translation function.
 * @param {User} user User to inspect.
 * @returns {string} Status label.
 */
function statusTextForUser(_, user) {
  const statusKey = statusKeyForUser(user)
  if (statusKey === "active") return _("Active")
  if (statusKey === "inactive") return _("Deactivated")
  return _("Invited")
}

/**
 * Builds the translated last-login label.
 * @param {(text: string, args?: Record<string, string>) => string} _ Translation function.
 * @param {User} user User to inspect.
 * @returns {string} Last-login label.
 */
function lastLoginText(_, user) {
  const lastLoginAt = user.currentSignInAt()
  if (!lastLoginAt) {
    return _("Last login: %{date}", {
      date: _("Never")
    })
  }
  return _("Last login: %{date}", {
    date: lastLoginAt.toLocaleString()
  })
}
